import {serialize, deserialize} from 'editor-core'
import {updateText, loadTemplate} from './editorActions'

/**
 * Create one editor runtime instance.
 * Runtime wires core ↔ adapters ↔ inputs and manages lifecycle.
 *
 * @param {{
 *   editorEngine: { dispatch: Function, undo: Function, redo: Function, getState: Function, subscribe: Function, replaceState: Function },
 *   canvasAdapter: object,
 *   persistentAdapter: object,
 *   devTools?: { getLog: Function, getStateAt: Function, replaceStateAt: Function, exportStateJSON: Function, getCurrentIndex: Function }
 * }} options
 */
export function createEditorRuntime({editorEngine, canvasAdapter, persistentAdapter, devTools}) {
    // Validate required parameters
    if (!editorEngine) throw new Error('editorEngine is required')
    if (!canvasAdapter) throw new Error('canvasAdapter is required')
    if (!persistentAdapter) throw new Error('persistentAdapter is required')

    // ---- document metadata (id, name, description, …) ----
    let documentMeta = null

    // ---- pending image files (blob files chưa upload) ----
    const pendingImageFiles = new Map()

    // ---- ephemeral UI state (not persisted, not undoable) ----
    let uiState = {textEditing: null}
    const uiStateListeners = new Set()

    let _measureTextNodeRect = null

    function notifyUiState() {
        for (const fn of uiStateListeners) fn(uiState)
    }

    function startTextEditing(nodeId) {
        const node = editorEngine.getState().nodes.get(nodeId)
        if (!node || node.type !== 'text') return
        uiState = {...uiState, textEditing: {nodeId}}
        notifyUiState()
    }

    // commitTextEditing(nodeId, finalText) — React View owns draftText and passes it here on commit.
    // Called with no args is a no-op / cancel (backward-compat for non-React callers).
    function commitTextEditing(nodeId, finalText) {
        if (!uiState.textEditing) return
        if (nodeId != null && finalText != null && finalText.trim() !== '') {
            editorEngine.dispatch(updateText(nodeId, finalText, {recordHistory: true}))
        }
        uiState = {...uiState, textEditing: null}
        notifyUiState()
    }

    function cancelTextEditing() {
        if (!uiState.textEditing) return
        uiState = {...uiState, textEditing: null}
        notifyUiState()
    }

    // ---- canvas-bound stuff (attached later) ----
    let mounted = false

    /**
     * Attach Konva canvas objects (called from React after mount).
     * Expects two layers: overlay and silhouette.
     * @param {HTMLElement} target - The DOM element to attach the canvas to
     * @throws {Error} If target is not provided
     */
    function attachCanvas(target) {
        if (!target) throw new Error('Canvas target element is required')
        canvasAdapter.attach(target, api)
    }
    function mount() {
        if (mounted) return
        mounted = true
        canvasAdapter.mount()
    }

    function unmount() {
        if (!mounted) return
        mounted = false
        canvasAdapter.unmount()
    }

    /**
     * Get silhouette layer as HTMLCanvasElement (for export pipeline).
     * Delegates to canvas adapter.
     * @param {{ x?: number, y?: number, width?: number, height?: number }} options - Optional content bounds to capture only that region
     * @returns {HTMLCanvasElement|null} Canvas with silhouette pixels, or null if not attached
     */
    function getSilhouetteCanvas(options) {
        return canvasAdapter.getSilhouetteCanvas?.(options) ?? null
    }

    /**
     * Get overlay layer as HTMLCanvasElement (for export pipeline).
     * Delegates to canvas adapter.
     * @param {{ x?: number, y?: number, width?: number, height?: number }} options - Optional content bounds to capture only that region
     * @returns {HTMLCanvasElement|null} Canvas with overlay pixels, or null if not attached
     */
    function getOverlayCanvas(options) {
        return canvasAdapter.getOverlayCanvas?.(options) ?? null
    }

    /**
     * Save document to persistent storage.
     * Merges current documentMeta with the provided metadata overrides.
     * After a successful save, documentMeta is updated with the merged result.
     * @param {object} [metadata] - Metadata overrides (e.g. { name, description })
     * @throws {Error} If save operation fails
     */
    async function saveDocument(metadata) {
        try {
            const state = editorEngine.getState()
            const doc = serialize(state)
            const merged = {...documentMeta, ...metadata}
            await persistentAdapter.save(doc, merged)
            documentMeta = merged
        } catch (error) {
            console.error('Failed to save document:', error)
            throw error
        }
    }

    /**
     * Load a document into the editor and set its metadata.
     * @param {object} doc - Document data to load
     * @param {object} [meta] - Optional metadata for the loaded document (e.g. { id, name, description })
     * @returns {boolean} True if document loaded successfully, false otherwise
     * @throws {Error} If load operation fails
     */
    async function loadDocument(doc, meta) {
        try {
            if (!doc) throw new Error('Document data is required')
            const loaded = await persistentAdapter.load(doc)
            if (!loaded) {
                console.warn('Failed to load document: persistentAdapter returned falsy value')
                return false
            }
            const currentState = editorEngine.getState()
            if (!currentState) throw new Error('Unable to get current editor state')
            const nextState = deserialize(loaded, currentState)
            if (!nextState) throw new Error('Deserialization resulted in invalid state')
            const nodes = Array.from(nextState.nodes.values())
            const order = nextState.order
            editorEngine.dispatch(loadTemplate(nodes, order))
            documentMeta = meta ?? null
            return true
        } catch (error) {
            console.error('Failed to load document:', error)
            throw error
        }
    }

    // ---- public API (thin proxies) ----
    const api = {
        attachCanvas,
        mount,
        unmount,

        setViewMode: canvasAdapter.setViewMode,
        getSilhouetteCanvas,
        getOverlayCanvas,
        getViewportCenter: () => canvasAdapter.getViewportCenter?.() ?? null,

        dispatch: editorEngine.dispatch,
        undo: editorEngine.undo,
        redo: editorEngine.redo,
        getState: editorEngine.getState,
        subscribe: editorEngine.subscribe,

        editor: editorEngine,
        interaction: canvasAdapter.getInteraction?.() ?? null,
        saveDocument,
        loadDocument,
        getDocumentMeta: () => documentMeta,

        // ---- pending image files ----
        pendingImageFiles,

        setDocumentMeta: (meta) => {
            documentMeta = meta
        },

        // ---- text editing session ----
        getUiState: () => uiState,
        subscribeUiState: (listener) => {
            uiStateListeners.add(listener)
            return () => uiStateListeners.delete(listener)
        },
        startTextEditing,
        commitTextEditing,
        cancelTextEditing,
        measureTextNodeRect: (nodeId) => _measureTextNodeRect?.(nodeId) ?? null,
        _setMeasureTextNodeRect: (fn) => {
            _measureTextNodeRect = fn
        },
    }
    if (devTools) {
        api.devTools = devTools
    }
    return api
}
